import React, { useState, useEffect, useMemo } from 'react';
import BBALoader from '../components/BBALoader';
import {
  FaChartArea,
  FaArrowDown,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaDollarSign,
  FaSearch,
} from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import { getAccounts, getAccountPayments } from '../services/firestoreService';
import { getAccountExpenses, getInternalExpenses } from '../services/accountExpensesService';
import {
  getRenewsOnDateFromAccount,
  getTermEndDateFromAccount,
} from '../utils/accountProgramDeliverables';
import { SIGNING_CHANCE_META, normalizeLeadSigningChance } from '../utils/leadSigningChance';
import {
  clientDisplayName,
  collectPaymentsForAccount,
  financeBillingSnapshot,
  formatCurrency,
  parsePaymentDate,
  resolveBillingAmounts,
} from '../utils/financeReportMetrics';
import PortalBackground from '../components/PortalBackground';
import FinanceProfitLossTab from '../components/crm/FinanceProfitLossTab';

function formatFinanceDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatBillingCycleLabel(cycle) {
  if (!cycle) return '—';
  const c = String(cycle).toLowerCase();
  if (c === 'monthly' || c === 'month') return 'Monthly';
  if (c === 'annual' || c === 'year') return 'Annual';
  if (c === 'quarterly' || c === 'quarter') return 'Quarterly';
  if (c === 'weekly' || c === 'week') return 'Weekly';
  return String(cycle);
}

/** Cash applied to subscription forecast bars — exclude one-off checkout products. */
function isSubscriptionForecastPayment(p) {
  const t = String(p?.type || '').toLowerCase();
  if (t === 'strategy_call') return false;
  return true;
}

function advanceBillingDate(anchor, billingCycle) {
  const c = (billingCycle || 'monthly').toLowerCase();
  const d = new Date(anchor);
  if (c === 'annual' || c === 'year') d.setFullYear(d.getFullYear() + 1);
  else if (c === 'quarterly' || c === 'quarter') d.setMonth(d.getMonth() + 3);
  else if (c === 'weekly' || c === 'week') d.setDate(d.getDate() + 7);
  else d.setMonth(d.getMonth() + 1);
  return d;
}

function normalizeFirstFutureRenewal(account) {
  const snap = financeBillingSnapshot(account);
  let cursor = snap.currentPeriodEnd
    ? new Date(snap.currentPeriodEnd)
    : getRenewsOnDateFromAccount(account);
  if (!cursor || Number.isNaN(cursor.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cycle = snap.billingCycle || 'monthly';
  while (cursor < today) {
    cursor = advanceBillingDate(cursor, cycle);
  }
  return cursor;
}

function projectionCutoffDate(account) {
  const b = account.billing || {};
  let cutoff = null;
  const termEnd = getTermEndDateFromAccount(account);
  if (termEnd && !Number.isNaN(termEnd.getTime())) cutoff = termEnd;

  if (b.cancelAtPeriodEnd && b.currentPeriodEnd) {
    const cpe = new Date(b.currentPeriodEnd);
    if (!Number.isNaN(cpe.getTime())) {
      cutoff = cutoff ? new Date(Math.min(cutoff.getTime(), cpe.getTime())) : cpe;
    }
  }
  if (b.cancelAt && !b.cancelAtPeriodEnd) {
    const ca = new Date(b.cancelAt);
    if (!Number.isNaN(ca.getTime())) {
      cutoff = cutoff ? new Date(Math.min(cutoff.getTime(), ca.getTime())) : ca;
    }
  }
  return cutoff;
}

/** Projected renewal { date, amount } for account from first future renewal through horizon */
function projectedRenewalsThrough(account, horizonEnd, subPayments) {
  const b = account.billing || {};
  if (!b.stripeSubscriptionId) return [];
  const st = (b.status || '').toLowerCase();
  if (st === 'canceled') return [];

  const { perCycle } = resolveBillingAmounts(account, subPayments);
  if (perCycle <= 0) return [];

  let cursor = normalizeFirstFutureRenewal(account);
  if (!cursor) return [];

  const cutoff = projectionCutoffDate(account);
  const cycle = financeBillingSnapshot(account).billingCycle || 'monthly';
  const out = [];
  while (cursor <= horizonEnd) {
    if (cutoff && cursor > cutoff) break;
    out.push({ date: new Date(cursor), amount: perCycle });
    cursor = advanceBillingDate(cursor, cycle);
  }
  return out;
}

/** Week starts Sunday (local), matching typical US calendar week. */
function startOfWeekSunday(dateAtMidnight) {
  const x = new Date(
    dateAtMidnight.getFullYear(),
    dateAtMidnight.getMonth(),
    dateAtMidnight.getDate(),
  );
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Current week + next 3 weeks (4 buckets). Returns weeks with labels and horizon for projection. */
function buildFourWeekBuckets() {
  const today0 = new Date();
  today0.setHours(0, 0, 0, 0);
  const w0 = startOfWeekSunday(today0);
  const weeks = [];
  let lastEnd = null;
  for (let i = 0; i < 4; i++) {
    const start = new Date(w0.getFullYear(), w0.getMonth(), w0.getDate() + i * 7);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
    lastEnd = end;
    const sameMonth = start.getMonth() === end.getMonth();
    const period = sameMonth
      ? `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.getDate()}, ${end.getFullYear()}`
      : `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    weeks.push({
      period,
      weekStart: start,
      weekEnd: end,
      projected: 0,
      received: 0,
    });
  }
  const horizonEnd = new Date(
    lastEnd.getFullYear(),
    lastEnd.getMonth(),
    lastEnd.getDate(),
    23,
    59,
    59,
    999,
  );
  return { weeks, horizonEnd };
}

/**
 * Break a month into week rows (Sunday-start, clipped to the month) so weekly
 * received/outstanding sums add up to the month exactly.
 */
function buildMonthWeekSegments(monthOffset) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
  const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const segments = [];
  let cursor = new Date(monthStart);
  while (cursor <= monthEnd) {
    const start = new Date(cursor);
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + (6 - start.getDay()));
    if (end > monthEnd) end.setTime(monthEnd.getTime());
    const monthShort = start.toLocaleDateString('en-US', { month: 'short' });
    segments.push({
      period:
        start.getDate() === end.getDate()
          ? `${monthShort} ${start.getDate()}`
          : `${monthShort} ${start.getDate()} – ${end.getDate()}`,
      weekStart: start,
      weekEnd: end,
      days: end.getDate() - start.getDate() + 1,
      daysInMonth: monthEnd.getDate(),
      projected: 0,
      received: 0,
    });
    cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
  }
  const horizonEnd = new Date(monthEnd.getFullYear(), monthEnd.getMonth(), monthEnd.getDate(), 23, 59, 59, 999);
  return { segments, horizonEnd, monthLabel };
}

/**
 * Projected-program financials for a lead — same amounts the Projected Program
 * card on Lead Overview shows (quarterly 10% off, annual 20% off).
 */
function leadProjectionFromProposal(lead) {
  const proposal = lead?.proposedProgram;
  if (!proposal) return null;
  const perMonth = Number(proposal.totalPricePerMonth) || 0;
  if (perMonth <= 0) return null;
  const cycle = (proposal.billingCycle || 'monthly').toLowerCase();
  const perCycle = cycle === 'quarterly' ? perMonth * 3 * 0.9 : cycle === 'annual' ? perMonth * 12 * 0.8 : perMonth;
  const mrr = cycle === 'quarterly' ? perMonth * 0.9 : cycle === 'annual' ? perMonth * 0.8 : perMonth;
  const name = (proposal.programName || 'Program').replace(/\s*Program\s*$/, '').trim() || 'Program';
  return { programName: name, cycle, perCycle, mrr };
}

/** States with no subscription MRR (churned or never completed checkout). */
const MRR_EXCLUDED_STATUSES = new Set(['canceled', 'cancelled', 'incomplete']);

const FinanceReports = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [leadAccounts, setLeadAccounts] = useState([]);
  const [paymentsByAccountId, setPaymentsByAccountId] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 5;
  const [forecastTab, setForecastTab] = useState('12month');
  const [forecastBarTooltip, setForecastBarTooltip] = useState(null);
  const [monthOffset, setMonthOffset] = useState(0);
  const [leadChanceFilter, setLeadChanceFilter] = useState('green');
  const [includeLeadsInForecast, setIncludeLeadsInForecast] = useState(false);
  const [expensesByAccountId, setExpensesByAccountId] = useState({});
  const [internalExpenses, setInternalExpenses] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const reportTab = searchParams.get('tab') === 'pnl' ? 'pnl' : 'cashflow';
  const setReportTab = (tab) => setSearchParams(tab === 'pnl' ? { tab: 'pnl' } : {}, { replace: true });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const accounts = await getAccounts();
        const clients = (accounts || []).filter((a) => a.type === 'Clients');
        setClientAccounts(clients);
        setLeadAccounts((accounts || []).filter((a) => a.type === 'Leads'));

        // Every client (not only Stripe-linked ones): P&L needs historical cash from ended
        // clients and expenses from all of them. Getters already swallow per-account errors.
        const [payEntries, expenseEntries, internal] = await Promise.all([
          Promise.all(clients.map(async (a) => [a.id, (await getAccountPayments(a.id)) || []])),
          Promise.all(clients.map(async (a) => [a.id, (await getAccountExpenses(a.id)) || []])),
          getInternalExpenses(),
        ]);
        setPaymentsByAccountId(Object.fromEntries(payEntries));
        setExpensesByAccountId(Object.fromEntries(expenseEntries));
        setInternalExpenses(internal);
      } catch (e) {
        console.error('FinanceReports load:', e);
        setClientAccounts([]);
        setLeadAccounts([]);
        setPaymentsByAccountId({});
        setExpensesByAccountId({});
        setInternalExpenses([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const stripeAccounts = useMemo(
    () => (clientAccounts || []).filter((a) => a.billing?.stripeSubscriptionId),
    [clientAccounts],
  );

  const horizonEnd = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth() + 12, 1);
  }, []);

  const pipelineData = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const months = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      months.push({
        period: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        monthStart: d,
        projected: 0,
        received: 0,
      });
    }

    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, horizonEnd, pay);

      for (let idx = 0; idx < 12; idx++) {
        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const i =
            (date.getFullYear() - start.getFullYear()) * 12 + (date.getMonth() - start.getMonth());
          if (i === idx) pSum += amount;
        });
        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const i =
            (payDate.getFullYear() - start.getFullYear()) * 12 + (payDate.getMonth() - start.getMonth());
          if (i === idx) rSum += amt;
        });
        months[idx].received += rSum;
        months[idx].projected += Math.max(0, pSum - rSum);
      }
    });

    return months;
  }, [stripeAccounts, horizonEnd, paymentsByAccountId]);

  const weeklyPipelineData = useMemo(() => {
    const { weeks, horizonEnd: wHorizon } = buildFourWeekBuckets();
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, wHorizon, pay);

      weeks.forEach((w) => {
        const ws = new Date(w.weekStart.getFullYear(), w.weekStart.getMonth(), w.weekStart.getDate());
        const we = new Date(w.weekEnd.getFullYear(), w.weekEnd.getMonth(), w.weekEnd.getDate());

        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          if (renewalDay >= ws && renewalDay <= we) pSum += amount;
        });

        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const day = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate());
          if (day >= ws && day <= we) rSum += amt;
        });

        w.received += rSum;
        w.projected += Math.max(0, pSum - rSum);
      });
    });

    return weeks;
  }, [stripeAccounts, paymentsByAccountId]);

  /** Selected month split into its weeks — received + outstanding per week. */
  const monthlyForecast = useMemo(() => {
    const { segments, horizonEnd: mHorizon, monthLabel } = buildMonthWeekSegments(monthOffset);
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const pay = paymentsByAccountId[account.id] || [];
      const merged = collectPaymentsForAccount(account, pay);
      const renewals = projectedRenewalsThrough(account, mHorizon, pay);

      segments.forEach((w) => {
        let pSum = 0;
        renewals.forEach(({ date, amount }) => {
          const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
          if (renewalDay >= w.weekStart && renewalDay <= w.weekEnd) pSum += amount;
        });

        let rSum = 0;
        merged.forEach((inv) => {
          if ((inv.status || '').toLowerCase() !== 'paid') return;
          if (!isSubscriptionForecastPayment(inv)) return;
          const payDate = parsePaymentDate(inv);
          if (!payDate) return;
          const amt = Math.abs(Number(inv.amount) || 0);
          if (amt <= 0) return;
          const day = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate());
          if (day >= w.weekStart && day <= w.weekEnd) rSum += amt;
        });

        w.received += rSum;
        w.projected += Math.max(0, pSum - rSum);
      });
    });
    return { segments, monthLabel };
  }, [stripeAccounts, paymentsByAccountId, monthOffset]);

  /** Leads with a Projected Program, filtered by signing chance (green, or green + yellow). */
  const projectedLeadRows = useMemo(() => {
    const allowed = leadChanceFilter === 'greenYellow' ? ['green', 'yellow'] : ['green'];
    return (leadAccounts || [])
      .map((lead) => {
        const projection = leadProjectionFromProposal(lead);
        if (!projection) return null;
        const chance = normalizeLeadSigningChance(lead);
        if (!allowed.includes(chance)) return null;
        return { leadId: lead.id, leadName: clientDisplayName(lead), chance, ...projection };
      })
      .filter(Boolean)
      .sort((a, b) => b.mrr - a.mrr);
  }, [leadAccounts, leadChanceFilter]);

  const leadsProjectedMrr = useMemo(
    () => projectedLeadRows.reduce((s, r) => s + r.mrr, 0),
    [projectedLeadRows],
  );

  const cashFlowMetrics = useMemo(() => {
    const today0 = new Date();
    today0.setHours(0, 0, 0, 0);
    const next30End = new Date(today0);
    next30End.setDate(next30End.getDate() + 30);
    const next90End = new Date(today0);
    next90End.setDate(next90End.getDate() + 90);
    const monthStart = new Date(today0.getFullYear(), today0.getMonth(), 1);
    const monthEnd = new Date(today0.getFullYear(), today0.getMonth() + 1, 0, 23, 59, 59);

    let receivedThisMonth = 0;
    stripeAccounts.forEach((a) => {
      const merged = collectPaymentsForAccount(a, paymentsByAccountId[a.id] || []);
      merged.forEach((p) => {
        const d = parsePaymentDate(p);
        if (!d || d < monthStart || d > monthEnd) return;
        if ((p.status || '').toLowerCase() !== 'paid') return;
        receivedThisMonth += Number(p.amount) || 0;
      });
    });

    let next30Sum = 0;
    let next90Sum = 0;
    let pipeline12 = 0;
    stripeAccounts.forEach((account) => {
      if ((account.billing?.status || '').toLowerCase() === 'canceled') return;
      const renewals = projectedRenewalsThrough(account, horizonEnd, paymentsByAccountId[account.id]);
      renewals.forEach(({ date, amount }) => {
        const renewalDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        if (renewalDay <= today0) return;
        if (renewalDay <= next30End) next30Sum += amount;
        if (renewalDay <= next90End) next90Sum += amount;
        pipeline12 += amount;
      });
    });

    return {
      receivedThisMonth,
      next30Sum,
      next90Sum,
      pipeline12,
    };
  }, [stripeAccounts, paymentsByAccountId, horizonEnd]);

  const projectRows = useMemo(() => {
    return stripeAccounts.map((account) => {
      const b = account.billing || {};
      const snap = financeBillingSnapshot(account);
      const subPay = paymentsByAccountId[account.id];
      const { perCycle, mrr } = resolveBillingAmounts(account, subPay);
      const renewals = projectedRenewalsThrough(account, horizonEnd, subPay).slice(0, 6);
      const plan = (b.planKey || 'subscription').replace(/_/g, ' ');
      const termEnd = getTermEndDateFromAccount(account);
      const renewsOn = getRenewsOnDateFromAccount(account);
      const startDate = account.program?.startDate || b.currentPeriodStart || null;
      return {
        accountId: account.id,
        projectLabel: plan,
        clientName: clientDisplayName(account),
        perCycle,
        mrr,
        billingCycleLabel: formatBillingCycleLabel(snap.billingCycle),
        startDate,
        termEndDate: termEnd ? termEnd.toISOString() : null,
        renewsOnDate: renewsOn ? renewsOn.toISOString() : null,
        renewals,
        status: (b.status || '—').replace(/_/g, ' '),
        rawStatus: (b.status || '').toLowerCase().trim(),
      };
    });
  }, [stripeAccounts, horizonEnd, paymentsByAccountId]);

  const filteredProjects = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return projectRows;
    return projectRows.filter(
      (r) =>
        r.clientName.toLowerCase().includes(q) || r.projectLabel.toLowerCase().includes(q),
    );
  }, [projectRows, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    setForecastBarTooltip(null);
  }, [forecastTab, monthOffset, includeLeadsInForecast, leadChanceFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / projectsPerPage));
  const indexOfLast = currentPage * projectsPerPage;
  const indexOfFirst = indexOfLast - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirst, indexOfLast);

  /** Match the client table: sum Monthly for every subscriber except canceled / incomplete, including past_due & unpaid. */
  const stripeMrr = useMemo(
    () =>
      projectRows
        .filter((r) => !MRR_EXCLUDED_STATUSES.has(r.rawStatus))
        .reduce((s, r) => s + r.mrr, 0),
    [projectRows],
  );

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative max-w-[1400px] mx-auto flex items-center justify-center h-64">
          <div className="text-center">
            <BBALoader size={48} className="mx-auto mb-4" />
            <p className={darkMode ? 'text-white' : 'text-gray-900'}>Loading finance reports…</p>
          </div>
        </div>
      </div>
    );
  }

  const baseForecastData =
    forecastTab === '4week'
      ? weeklyPipelineData
      : forecastTab === 'monthly'
        ? monthlyForecast.segments
        : pipelineData;

  // Layer projected lead revenue onto each bucket when toggled on (never onto past months).
  const activeForecastData = baseForecastData.map((d) => {
    let leads = 0;
    if (includeLeadsInForecast && leadsProjectedMrr > 0) {
      if (forecastTab === '12month') leads = leadsProjectedMrr;
      else if (forecastTab === '4week') leads = (leadsProjectedMrr * 12) / 52;
      else if (monthOffset >= 0) leads = leadsProjectedMrr * (d.days / d.daysInMonth);
    }
    return { ...d, leads };
  });
  const maxForecastBar = Math.max(
    ...activeForecastData.map((d) => d.projected + d.received + d.leads),
    1,
  );
  const forecastTotals = activeForecastData.reduce(
    (acc, d) => {
      acc.received += d.received;
      acc.projected += d.projected;
      acc.leads += d.leads;
      return acc;
    },
    { received: 0, projected: 0, leads: 0 },
  );
  const forecastGrandTotal = forecastTotals.received + forecastTotals.projected + forecastTotals.leads;

  return (
    <div className="p-6 min-h-screen relative text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <BackButton onClick={() => navigate('/crm/reports')} />
        <h1 className={`text-3xl font-bold tracking-tight text-center flex-1 text-gray-900 dark:text-white`}>
          Finance Reports
        </h1>
        <div className="w-10" aria-hidden />
      </div>

      <div className="flex justify-center mb-8">
        <div
          className="inline-flex rounded-xl p-1 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm"
          role="tablist"
          aria-label="Finance report"
        >
          {[
            { key: 'cashflow', label: 'Cash Flow' },
            { key: 'pnl', label: 'Profit & Loss' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={reportTab === t.key}
              onClick={() => setReportTab(t.key)}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${
                reportTab === t.key
                  ? 'bg-red-600 text-white shadow'
                  : 'text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {reportTab === 'pnl' ? (
        <FinanceProfitLossTab
          clientAccounts={clientAccounts}
          paymentsByAccountId={paymentsByAccountId}
          expensesByAccountId={expensesByAccountId}
          internalExpenses={internalExpenses}
          darkMode={darkMode}
        />
      ) : (
      <>

      <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8`}>
        <h2 className={`text-xl font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Cash Flow Summary
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <FaArrowDown className={`mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Received this month
              </span>
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(cashFlowMetrics.receivedThisMonth)}
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <FaDollarSign className={`mr-2 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                MRR
              </span>
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(stripeMrr)}
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <FaCalendarAlt className={`mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Next 30 days
              </span>
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(cashFlowMetrics.next30Sum)}
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <FaClock className={`mr-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Next 90 days
              </span>
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(cashFlowMetrics.next90Sum)}
            </div>
          </div>

          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center mb-2">
              <FaChartArea className={`mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Outstanding pipeline
              </span>
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(cashFlowMetrics.pipeline12)}
            </div>
          </div>
        </div>
      </div>

      <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8`}>
        <div className="flex justify-center mb-4">
          <div
            className={`inline-flex rounded-lg p-1 border ${
              darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-100'
            }`}
            role="tablist"
            aria-label="Forecast range"
          >
            <button
              type="button"
              role="tab"
              aria-selected={forecastTab === 'monthly'}
              onClick={() => setForecastTab('monthly')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                forecastTab === 'monthly'
                  ? 'bg-red-600 text-white shadow'
                  : darkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              Monthly Forecast
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={forecastTab === '4week'}
              onClick={() => setForecastTab('4week')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                forecastTab === '4week'
                  ? 'bg-red-600 text-white shadow'
                  : darkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              4 Week Forecast
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={forecastTab === '12month'}
              onClick={() => setForecastTab('12month')}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                forecastTab === '12month'
                  ? 'bg-red-600 text-white shadow'
                  : darkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              12-Month Forecast
            </button>
          </div>
        </div>
        {forecastTab === 'monthly' && (
          <div className="flex items-center justify-center gap-3 mb-4">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonthOffset((m) => m - 1)}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <FaChevronLeft />
            </button>
            <span className={`text-sm font-semibold min-w-[140px] text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {monthlyForecast.monthLabel}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonthOffset((m) => m + 1)}
              className={`p-2 rounded-lg transition-colors ${
                darkMode
                  ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <FaChevronRight />
            </button>
            {monthOffset !== 0 && (
              <button
                type="button"
                onClick={() => setMonthOffset(0)}
                className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                This month
              </button>
            )}
          </div>
        )}
        {forecastGrandTotal > 0 && (
          <div
            className={`flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6 px-4 py-3 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${darkMode ? 'bg-green-500' : 'bg-green-600'}`} />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Received</span>
              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(forecastTotals.received)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded ${darkMode ? 'bg-red-500' : 'bg-red-600'}`} />
              <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Outstanding</span>
              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(forecastTotals.projected)}
              </span>
            </div>
            {forecastTotals.leads > 0 && (
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${darkMode ? 'bg-amber-400' : 'bg-amber-500'}`} />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Projected sales</span>
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(forecastTotals.leads)}
                </span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Total{' '}
                {forecastTab === 'monthly'
                  ? `· ${monthlyForecast.monthLabel}`
                  : forecastTab === '4week'
                    ? '· next 4 weeks'
                    : '· next 12 months'}
              </span>
              <span className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(forecastGrandTotal)}
              </span>
            </div>
          </div>
        )}
        {activeForecastData.some((row) => row.projected > 0 || row.received > 0 || row.leads > 0) ? (
          <div className="relative">
            <div className="h-64 flex items-end justify-between space-x-2 pt-8">
              {activeForecastData.map((data, index) => {
                const chartHeight = 200;
                const leadsAmt = data.leads || 0;
                const total = data.projected + data.received + leadsAmt;
                const totalHeight =
                  maxForecastBar > 0 ? (total / maxForecastBar) * chartHeight : 0;
                const minBarHeight = 20;
                const stackHeight = Math.max(totalHeight, total > 0 ? minBarHeight : 0);
                const segHeight = (value) => (total > 0 ? (value / total) * stackHeight : 0);
                const hRec = segHeight(data.received);
                const hProj = segHeight(data.projected);
                const hLeads = segHeight(leadsAmt);
                const recGreen = darkMode ? 'bg-green-500' : 'bg-green-600';
                const projRed = darkMode ? 'bg-red-500' : 'bg-red-600';
                const leadsAmber = darkMode ? 'bg-amber-400' : 'bg-amber-500';
                return (
                  <div
                    key={index}
                    className="flex-1 flex flex-col items-center min-w-0"
                    onMouseLeave={() => setForecastBarTooltip(null)}
                  >
                    <div className="relative w-full flex flex-col justify-end" style={{ height: `${chartHeight}px` }}>
                      {forecastBarTooltip?.index === index && (
                        <div
                          role="tooltip"
                          className={`absolute left-1/2 top-1 z-20 -translate-x-1/2 px-2.5 py-1 rounded-md text-xs font-semibold shadow-lg whitespace-nowrap pointer-events-none border ${
                            darkMode
                              ? 'bg-gray-700 text-gray-100 border-gray-600'
                              : 'bg-white text-gray-900 border-gray-200'
                          }`}
                        >
                          {forecastBarTooltip.kind === 'received'
                            ? 'Received'
                            : forecastBarTooltip.kind === 'leads'
                              ? 'Projected sales'
                              : 'Outstanding'}
                          : {formatCurrency(forecastBarTooltip.amount)}
                        </div>
                      )}
                      <div
                        className={`absolute -top-10 left-1/2 transform -translate-x-1/2 text-xs font-medium whitespace-nowrap ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        {formatCurrency(total)}
                      </div>
                      {total === 0 ? (
                        <div
                          className={`w-full rounded-t ${darkMode ? 'bg-gray-400' : 'bg-gray-300'} transition-all duration-300`}
                          style={{ height: `${minBarHeight}px` }}
                        />
                      ) : (
                        <div
                          className="w-full overflow-hidden rounded-t flex flex-col justify-end transition-all duration-300"
                          style={{ height: `${stackHeight}px`, minHeight: minBarHeight }}
                        >
                          {leadsAmt > 0 && (
                            <div
                              className={`w-full ${leadsAmber} cursor-default`}
                              style={{ height: `${hLeads}px` }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'leads',
                                  amount: leadsAmt,
                                })
                              }
                            />
                          )}
                          {data.received > 0 && (
                            <div
                              className={`w-full ${recGreen} cursor-default`}
                              style={{ height: `${hRec}px` }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'received',
                                  amount: data.received,
                                })
                              }
                            />
                          )}
                          {data.projected > 0 && (
                            <div
                              className={`w-full ${projRed} cursor-default`}
                              style={{ height: `${hProj}px` }}
                              onMouseEnter={() =>
                                setForecastBarTooltip({
                                  index,
                                  kind: 'outstanding',
                                  amount: data.projected,
                                })
                              }
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      className={`text-xs mt-2 text-center leading-tight px-0.5 break-words ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      {data.period}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-4">
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded ${darkMode ? 'bg-green-500' : 'bg-green-600'}`} />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Received (paid invoices)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`w-4 h-4 rounded ${darkMode ? 'bg-red-500' : 'bg-red-600'}`} />
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Outstanding renewals (net of same-period paid invoices per client)
                </span>
              </div>
              {includeLeadsInForecast && (
                <div className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded ${darkMode ? 'bg-amber-400' : 'bg-amber-500'}`} />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Projected sales ({leadChanceFilter === 'greenYellow' ? 'green + yellow' : 'green'} leads)
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`h-64 flex items-center justify-center text-lg ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}
          >
            {forecastTab === '4week'
              ? 'No payments or projected renewals in the next four weeks (sync Stripe on client accounts).'
              : forecastTab === 'monthly'
                ? `No payments or projected renewals in ${monthlyForecast.monthLabel}.`
                : 'No payments or projected renewals in the next twelve months (sync Stripe on client accounts).'}
          </div>
        )}
      </div>

      <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-8`}>
        <div className="text-center mb-6">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Projected Sales
          </h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Leads with a Projected Program, filtered by signing chance. Toggle them into the
            forecast above to see cash flow if they sign.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-6">
          <div
            className={`inline-flex rounded-lg p-1 border ${
              darkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-100'
            }`}
            role="tablist"
            aria-label="Signing chance filter"
          >
            {[
              { key: 'green', label: 'Green only', dots: ['green'] },
              { key: 'greenYellow', label: 'Green + Yellow', dots: ['green', 'yellow'] },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                role="tab"
                aria-selected={leadChanceFilter === opt.key}
                onClick={() => setLeadChanceFilter(opt.key)}
                className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors flex items-center gap-2 ${
                  leadChanceFilter === opt.key
                    ? 'bg-red-600 text-white shadow'
                    : darkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                <span className="flex items-center gap-1">
                  {opt.dots.map((c) => (
                    <span
                      key={c}
                      className="inline-block w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: SIGNING_CHANCE_META[c].dotColor }}
                    />
                  ))}
                </span>
                {opt.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={includeLeadsInForecast}
            onClick={() => setIncludeLeadsInForecast((v) => !v)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg border transition-colors ${
              includeLeadsInForecast
                ? darkMode
                  ? 'bg-amber-500/20 border-amber-400 text-amber-300'
                  : 'bg-amber-50 border-amber-500 text-amber-700'
                : darkMode
                  ? 'border-gray-600 text-gray-300 hover:text-white'
                  : 'border-gray-300 text-gray-700 hover:text-gray-900'
            }`}
          >
            {includeLeadsInForecast ? '✓ Included in forecast above' : 'Include in forecast above'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className={`text-sm font-medium text-center mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Projected MRR
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(leadsProjectedMrr)}
            </div>
          </div>
          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className={`text-sm font-medium text-center mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Projected / year
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {formatCurrency(leadsProjectedMrr * 12)}
            </div>
          </div>
          <div
            className={`p-4 rounded-lg border ${
              darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className={`text-sm font-medium text-center mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Leads included
            </div>
            <div className={`text-2xl font-bold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {projectedLeadRows.length}
            </div>
          </div>
        </div>

        {projectedLeadRows.length === 0 ? (
          <div className={`py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No {leadChanceFilter === 'greenYellow' ? 'green or yellow' : 'green'} leads with a
            Projected Program yet. Set a proposed program on a lead to see it here.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <table
              className={`min-w-[600px] w-full divide-y text-sm ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}
            >
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  {['Lead', 'Signing chance', 'Program', 'Per cycle', 'Monthly'].map((h) => (
                    <th
                      key={h}
                      className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                        darkMode ? 'text-gray-300' : 'text-gray-500'
                      }`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}
              >
                {projectedLeadRows.map((row) => (
                  <tr
                    key={row.leadId}
                    className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                    onClick={() => navigate(`/crm/lead/${row.leadId}`)}
                  >
                    <td className={`px-3 py-3 whitespace-nowrap font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {row.leadName}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: SIGNING_CHANCE_META[row.chance].dotColor }}
                        />
                        <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                          {SIGNING_CHANCE_META[row.chance].label}
                        </span>
                      </span>
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {row.programName}
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                      {formatCurrency(row.perCycle)}
                      <span className={`text-xs ml-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        / {row.cycle === 'annual' ? 'year' : row.cycle === 'quarterly' ? 'quarter' : 'month'}
                      </span>
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(row.mrr)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
        <div className="text-center mb-6">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Clients</h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Stripe subscriptions from your client accounts. Open a row for billing details or go to{' '}
            <button
              type="button"
              onClick={() => navigate('/crm/clients')}
              className="text-red-600 dark:text-red-400 hover:underline font-medium"
            >
              Accounts
            </button>
            .
          </p>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1">
            <FaSearch
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            />
            <input
              type="text"
              placeholder="Search clients or plan…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
            />
          </div>
        </div>

        <div className="overflow-x-auto -mx-2 px-2">
          <table
            className={`min-w-[900px] w-full divide-y text-sm ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}
          >
            <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Client
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Monthly
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Cycle
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Start
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Minimum term
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Renews on
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Status
                </th>
                <th
                  className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider min-w-[200px] ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  Upcoming renewals
                </th>
              </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {currentProjects.map((row) => (
                <tr
                  key={row.accountId}
                  className={`${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} cursor-pointer transition-colors`}
                  onClick={() => navigate(`/crm/client/${row.accountId}`)}
                >
                  <td className={`px-3 py-3 whitespace-nowrap font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {row.clientName}
                  </td>
                  <td className={`px-3 py-3 whitespace-nowrap font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(row.mrr)}
                    <span className={`block text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formatCurrency(row.perCycle)} / cycle
                    </span>
                  </td>
                  <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {row.billingCycleLabel}
                  </td>
                  <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatFinanceDate(row.startDate)}
                  </td>
                  <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatFinanceDate(row.termEndDate)}
                  </td>
                  <td className={`px-3 py-3 whitespace-nowrap ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {formatFinanceDate(row.renewsOnDate)}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded text-xs capitalize ${darkMode ? 'bg-gray-600 text-gray-100' : 'bg-gray-200 text-gray-800'}`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className={`px-3 py-3 ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {row.renewals.length === 0 ? (
                      <span className="italic text-xs">None in 12 mo window</span>
                    ) : (
                      <div className="space-y-1 text-xs">
                        {row.renewals.map((u, i) => (
                          <div key={i} className="flex justify-between items-center gap-2">
                            <span>{formatFinanceDate(u.date)}</span>
                            <span className="font-medium shrink-0">{formatCurrency(u.amount)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {currentProjects.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className={`px-6 py-4 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {searchTerm
                      ? 'No projects match your search'
                      : 'No Stripe subscriptions yet. Link checkout or sync billing on a client.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-6">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                currentPage === 1
                  ? 'text-gray-400 bg-gray-200 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'text-gray-700 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Previous
            </button>
            <span className={`mx-4 text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                currentPage === totalPages
                  ? 'text-gray-400 bg-gray-200 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                  : 'text-gray-700 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
      </>
      )}
      </div>
    </div>
  );
};

export default FinanceReports;
